import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
  ScaleControl,
  useMap
} from "react-leaflet";
import { FaCircleInfo } from "react-icons/fa6";
import "leaflet/dist/leaflet.css";
// import "../TsunamiThreat.css"; // Ensure you have your CSS file
import L from 'leaflet';
import { MapHomeControl, MapLegendControl } from "../../../../common/MapControls/MapControls";
// --- A pre-defined color palette for the categories ---
const COLOR_PALETTE = [
  "#3366cc",
  "#dc3912",
  "#ff9900",
  "#109618",
  "#990099",
  "#0099c6",
  "#dd4477",
  "#66aa00",
  "#b82e2e",
  "#316395",
  "#994499",
  "#22aa99",
  "#aadd11",
  "#ee3377",
  "#44ccaa",
  "#7755aa",
  "#bb6622",
  "#4477aa",
]; // Expanded for more unique colors if needed

// New component to handle map bounds imperatively
const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { animate: false });
    }
  }, [map, bounds]);

  return null;
};

// --- Dynamic Legend Component ---
const Legend = ({ colorMap }) => {
  if (Object.keys(colorMap).length === 0) {
    return null;
  }
  const items = Object.entries(colorMap).map(([name, color]) => ({ label: name, color }));
  return <MapLegendControl sections={[{ shape: "square", items }]} />;
};


const LandCover = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [featLandCover, setFeatLandCover] = useState(null);
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);

  // --- State to hold categorized data and color mappings ---
  const [groupedLandCover, setGroupedLandCover] = useState({});
  const [colorMap, setColorMap] = useState({});
  const [mapBounds, setMapBounds] = useState(null);

  useEffect(() => {
    // Load the GeoJSON boundary data
    fetch("/DATA/OSMbased_adm1_boundary.json")
      .then((response) => response.json())
      .then((data) => setBoundaryData(data))
      .catch((error) => console.error("Error loading boundary data:", error));
  }, []);

  // --- Fetch and process Land Cover Data ---
  useEffect(() => {
    fetch("/Earthquake/land_cover_2011.geojson")
      .then((response) => response.json())
      .then((data) => {
        const groups = {};
        const colors = {};
        let colorIndex = 0;

        // Sort categories alphabetically for a consistent color assignment and legend order
        const categories = [
          ...new Set(data.features.map((f) => f.properties.LU_CATEGOR)),
        ].sort();

        categories.forEach((category) => {
          if (!category) return;

          groups[category] = {
            type: "FeatureCollection",
            features: data.features.filter(
              (f) => f.properties.LU_CATEGOR === category
            ),
          };
          colors[category] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
          colorIndex++;
        });
        
        // ADDED: Logic to calculate and set bounds
        if (data && data.features.length > 0) {
          const geoJsonLayer = L.geoJSON(data);
          setMapBounds(geoJsonLayer.getBounds());
        }

        setGroupedLandCover(groups);
        setColorMap(colors);
      })
      .catch((error) => console.error("Error loading land cover data:", error));
  }, []);

  const onEachLandCoverFeature = (feature, layer) => {
    layer.on({
      mouseover: (e) => {
        // Highlight on hover
        e.target.setStyle({
          weight: 3,
          color: "#666",
          dashArray: "",
          fillOpacity: 0.9,
        });

        setFeatLandCover(
          ` <table class="table-popup"> <tbody>
                    <tr class="centeralign">
                        <th colspan="2"><h6>Land Cover</h6></th>
                    </tr>
                    <tr><th>Category : </th><td>${feature.properties.LU_CATEGOR}</td></tr>
                    <tr><th>Class : </th><td>${feature.properties.LU_CLASS}</td></tr>
                    <tr><th>Area (km²) : </th><td>${feature.properties.AREA_KM2.toFixed(
                      2
                    )}</td></tr>
                    <tr><th>Hectares : </th><td>${feature.properties.HECTARES.toFixed(
                      2
                    )}</td></tr>
                    </tbody> </table>`
        );
      },
      mouseout: (e) => {
        // Reset style on mouseout
        e.target.setStyle({
          weight: 1,
          color: "black",
          dashArray: "",
          fillOpacity: 0.7,
        });
        setFeatLandCover(null);
      },
      click: (e) => {
        e.target._map.fitBounds(e.target.getBounds());
      },
    });
  };
  const InfoButton = ({ onClick }) => {
    // const map = useMap();
    // Leaflet's own drag/click handling swallows clicks on overlay controls
    // unless propagation to the map is explicitly disabled on the DOM node.
    const btnRef = useCallback((node) => {
      if (node) {
        L.DomEvent.disableClickPropagation(node);
        L.DomEvent.disableScrollPropagation(node);
      }
    }, []);
    return (
      <div
        ref={btnRef}
        style={{
          position: "absolute",
          bottom: "20px",
          left: "12px",
          padding: "8px",
          borderRadius: "8px",
          fontSize: "15px",
          cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
          zIndex: 999,
        }}
        onClick={onClick}
      >
        <FaCircleInfo />
      </div>
    );
  };

  return (
    <div className="wind-speed-cyclone-container">
      <div className="map-container-WindSpeedCyclone">
        <MapContainer
          // REMOVED center and zoom props
          // center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          // zoom={INITIAL_VIEW_STATE.zoom}
          
          attributionControl={false}
          style={{ width: "100%", height: "650px" }}
        >
          <MapHomeControl />
          {/* ADDED: Map bounds updater component */}
          <MapBoundsUpdater bounds={mapBounds} />

          {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              This page displays static Land Cover data from 2011, including
              multiple parameters.
            </div>
          )}
          <InfoButton onClick={() => setInfo(!info)} />
          {featLandCover && (
            <div
              className="hover-box"
              dangerouslySetInnerHTML={{ __html: featLandCover }}
            />
          )}
          <LayersControl position="topright">
            <LayersControl.BaseLayer name="White Theme" checked>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>

            {Object.entries(groupedLandCover).map(([category, geojsonData]) => (
              <LayersControl.Overlay name={category} key={category} checked>
                <GeoJSON
                  data={geojsonData}
                  style={{
                    color: "black",
                    weight: 1,
                    opacity: 0.5,
                    fillOpacity: 0.7,
                    fillColor: colorMap[category], // Use the assigned color
                  }}
                  onEachFeature={onEachLandCoverFeature}
                />
              </LayersControl.Overlay>
            ))}
          </LayersControl>
          {boundaryData && (
            <GeoJSON
              data={boundaryData}
              style={{
                color: "#000000",
                weight: 1.5,
                opacity: 0.8,
                fillOpacity: 0,
              }}
            />
          )}
          <ScaleControl position="bottomleft" />
          <Legend colorMap={colorMap} />
        </MapContainer>
      </div>
    </div>
  );
};

export default LandCover;